import { Toast } from 'antd-mobile';

/**
 * 把毫秒格式化
 * @param {number} time 毫秒
 * @return {string}
 */
// 使用传统函数声明代替箭头函数，兼容ES2015
export function formatTime(time: number | undefined) {
  if (!time && typeof time !== 'number') {
    return '';
  }
  if (time <= 0) {
    return '00:00:00';
  }
  const hours = Math.floor(time / 1000 / 3600);
  const minutes = Math.floor(time / 1000 / 60) % 60;
  const seconds = Math.floor((time / 1000) % 60);

  function format(v: number) {
    // 使用传统字符串拼接代替模板字符串，兼容ES2015
    return v >= 10 ? v : '0' + v;
  }

  // 使用传统字符串拼接代替模板字符串，兼容ES2015
  return format(hours) + ':' + format(minutes) + ':' + format(seconds);
}

/**
 * 将任意输入转换为 'YYYY-MM-DD HH:mm' 格式
 * @param value 任意值 (数字、字符串数字、无效值等)
 * @returns 格式化后的日期字符串，若输入无效返回 ''
 */
export const formatTimestamp = (value: unknown): string => {
  // 1. 尝试将输入转为数字
  let ts = Number(value);

  // 2. 校验：如果是 0, NaN, 或转换失败，直接返回空字符串
  if (!ts || isNaN(ts)) {
    return '';
  }

  // 3. 兼容处理：如果是 10 位数字（秒级），转换为毫秒
  // 使用 String(ts) 可能会有科学计数法问题(极少见)，但对时间戳通常安全
  if (String(ts).length === 10) {
    ts *= 1000;
  }

  const dateObj = new Date(ts);

  // 4. 再次校验：确保生成的 Date 对象是有效的 (排除极端的越界数字)
  if (isNaN(dateObj.getTime())) {
    return '';
  }

  // 5. 格式化逻辑
  const year = dateObj.getFullYear();
  const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
  const day = dateObj.getDate().toString().padStart(2, '0');
  const hours = dateObj.getHours().toString().padStart(2, '0');
  const minutes = dateObj.getMinutes().toString().padStart(2, '0');

  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

// 使用传统函数声明代替箭头函数，兼容ES2015
export function getPosPercent(duration: number, currentTime: number) {
  return (currentTime * 100) / (duration || -1);
}

// 使用传统函数声明代替箭头函数，兼容ES2015
export function isInRange(time: number, start: number, end: number): boolean {
  return time >= start && time <= end;
}

// 使用传统函数声明代替箭头函数，兼容ES2015
export function getTimeScale(
  currentTime: number,
  startTime: number,
  endTime: number
) {
  const isActiveTime = isInRange(currentTime, startTime, endTime);
  const timeDiff = endTime - startTime;
  const timeDiffWithCurrent = currentTime - startTime;
  const currentScalePercent = isActiveTime ? timeDiffWithCurrent / timeDiff : 0;

  return currentScalePercent;
}
export function parseResponseData(
  val: any,
  contentType?: string | null
): Promise<any> {
  return new Promise(function (resolve) {
    const ct = contentType || '';
    if (
      ct.indexOf('application/x-www-form-urlencoded') >= 0 ||
      ct.indexOf('text/plain') >= 0
    ) {
      const resp = val as Response;
      resp.text().then(function (txt) {
        resolve(txt);
      });
    } else if (ct.indexOf('application/json') >= 0) {
      const resp = val as Response;
      resp.json().then(function (json) {
        resolve(json);
      });
    } else {
      resolve(val ? String(val) : '');
    }
  });
}

// 使用传统函数声明代替箭头函数，兼容ES2015
export function formatPois(poisList: Array<any>) {
  // 使用条件检查代替可选链操作符
  if (!poisList) {
    return [];
  }

  return poisList.map(function (item, index) {
    return {
      value: item.lonlat + ':' + index,
      label: item.name + '-' + item.address
    };
  });
}

// 使用传统函数声明代替箭头函数，兼容ES2015
export function splitString(str: string) {
  let parts = [];
  const dashIndex = str.indexOf('-');

  if (dashIndex !== -1) {
    // 如果存在'-'，则从'-'处分割
    parts.push(str.substring(0, dashIndex - 4));
    parts.push(str.substring(dashIndex - 4, dashIndex));
    parts.push(str.substring(dashIndex));
  } else {
    // 如果不存在'-'，则从最后4位分割
    parts.push(str.substring(0, str.length - 4));
    parts.push(str.substring(str.length - 4));
  }

  return parts;
}

/**
 * 判断一个值是否为空。
 * @param value - 要检查的值。
 * @returns 如果是数组或字符串且长度为0，或者是对象且没有属性，则返回true；否则返回false。
 */
// 使用传统函数声明代替箭头函数，兼容ES2015
export function isEmpty(value: any) {
  if (value == null) {
    return true;
  }
  if (Array.isArray(value) || typeof value === 'string') {
    return value.length === 0;
  }
  if (value instanceof Map || value instanceof Set) {
    return value.size === 0;
  }
  if (Object.prototype.toString.call(value) === '[object Object]') {
    return Object.keys(value).length === 0;
  }
  return false;
}

export function copyText(text: string) {
  let rs = false;

  if (navigator.clipboard && navigator.clipboard.writeText) {
    navigator.clipboard
      .writeText(text)
      .then(function () {
        rs = true;
        Toast.show({
          icon: 'success',
          content: '复制成功'
        });
      })
      .catch(function (err) {
        console.warn(err);
        fallbackCopyText();
      });
  } else {
    fallbackCopyText();
  }

  // 定义回退复制方法
  function fallbackCopyText() {
    const fakeElement = document.createElement('textarea');
    fakeElement.value = text;
    fakeElement.style.top = '0';
    fakeElement.style.left = '-100vw';
    fakeElement.style.position = 'fixed';
    fakeElement.setAttribute('readonly', '');
    document.body.appendChild(fakeElement);
    fakeElement.select();
    fakeElement.setSelectionRange(0, fakeElement.value.length);

    try {
      // 虽然execCommand已被弃用，但在某些旧浏览器中仍然是唯一的选择
      rs = document.execCommand('copy');
      if (rs) {
        Toast.show({
          icon: 'success',
          content: '复制成功'
        });
      } else {
        Toast.show({
          icon: 'fail',
          content: '复制失败'
        });
      }
    } catch (e) {
      console.warn(e);
      Toast.show({
        icon: 'fail',
        content: '复制失败'
      });
    }

    fakeElement.remove();
    const sel = window.getSelection ? window.getSelection() : null;
    if (sel && sel.removeAllRanges) {
      sel.removeAllRanges();
    }
  }
}

/**
 * 判断是否为数值（支持数字或可转换为数字的字符串）。
 * - number 且有限 => true
 * - string 去空格后可被 Number 转换且有限 => true
 * - 其他或 NaN/Infinity/空串 => false
 */
export function isNumeric(value: any): boolean {
  if (typeof value === 'number') {
    return Number.isFinite(value);
  }
  if (typeof value === 'string') {
    var s = value.trim();
    if (s === '') return false;
    var n = Number(s);
    return Number.isFinite(n);
  }
  return false;
}

/**
 * 判断一个值是否为“异常值”。
 * 异常值的定义：undefined、null、NaN（仅限 number 类型的 NaN）。
 * @param value 任何待检测的值
 * @returns 若为 undefined/null/NaN 返回 true，否则返回 false
 */
export function isAbnormalValue(value: any): boolean {
  // 注意：Number.isNaN 只有在参数为 number 且为 NaN 时才返回 true
  return (
    value === undefined ||
    value === null ||
    (typeof value === 'number' && Number.isNaN(value))
  );
}

/**
 * 格式化包裹号码。
 * @param {string} packageNo - 包裹号码。
 * @returns {string} - 格式化后的包裹号码。
 */
// 使用传统函数声明代替箭头函数，兼容ES2015
export function formatPackageNo(packageNo: string): string {
  // 使用条件检查代替可选链操作符
  if (!packageNo) {
    return '';
  }

  // 先替换破折号及其后面的内容
  let result = packageNo.replace(/-.*/, '');
  // 再替换最后四位数字为带强调的数字
  result = result.replace(/(\d{4})$/, '<strong>$1</strong>');
  return result;
}

// 将角度转换为弧度
// 使用传统函数声明代替箭头函数，兼容ES2015
function deg2rad(deg: number) {
  return deg * (Math.PI / 180);
}
// 两点之间的距离，返回距离，单位为米
// 使用传统函数声明代替箭头函数，兼容ES2015
export function getDistanceInMeters(
  lat1: any,
  lon1: any,
  lat2: any,
  lon2: any
) {
  // 入参校验：支持 number 和可被转换为 number 的字符串
  if (
    !isNumeric(lat1) ||
    !isNumeric(lon1) ||
    !isNumeric(lat2) ||
    !isNumeric(lon2)
  ) {
    return null;
  }
  const R = 6371; // 地球平均半径，单位为公里
  const dLat = deg2rad(lat2 - lat1);
  const dLon = deg2rad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c;
  return d * 1000;
}

/**
 * 将米转换为千米，默认保留两位小数。
 * @param distance 距离，支持 number 或可转换为数字的字符串
 * @param precision 小数精度，默认 2，范围 0-10
 * @returns number | null 当入参非法时返回 null
 */
export function metersToKilometers(
  distance: any,
  precision?: number
): number | null {
  if (!isNumeric(distance)) {
    return null;
  }

  const value = Number(distance);
  if (!Number.isFinite(value)) {
    return null;
  }

  let safePrecision = 2;
  if (typeof precision === 'number' && Number.isFinite(precision)) {
    if (precision < 0) {
      safePrecision = 0;
    } else if (precision > 10) {
      safePrecision = 10;
    } else {
      safePrecision = Math.floor(precision);
    }
  }

  const kilometers = value / 1000;
  const factor = Math.pow(10, safePrecision);
  return Math.round(kilometers * factor) / factor;
}

// 已经使用传统函数声明，保持不变
export function calculateSpeed(y: number): number {
  // 定义sigmoid函数的斜率参数k，这里需要调整以确保y=±1时speed接近±1.2
  const k: number = Math.log(2); // 调整k值以使得e^(-k*1)接近0.5

  // 实现调整后的sigmoid函数来计算速度
  let speed: number = -2.4 / (1 + Math.exp(-k * y)) + 1.2;
  speed *= 3;
  // 保留两位小数
  speed = parseFloat(speed.toFixed(2));

  return speed;
}
// 已经使用传统函数声明，保持不变
export function calculateAngle(x: number): number {
  // 定义函数的斜率参数k
  const k: number = 1;
  // 定义角度的最大值C
  const C = 33;

  // 实现调整后的sigmoid函数来计算转向角度
  let angle: number = C * (2 / (1 + Math.exp(-k * x)) - 1);

  angle *= (1.95 * Math.PI) / 180;
  angle *= -1;
  return angle;
}

// 使用传统函数声明代替箭头函数，兼容ES2015
export function setScreenOrientation(orientation: number) {
  // 使用条件检查代替可选链操作符和短路运算符
  if (window && (window as any).app && (window as any).app.postMessage) {
    (window as any).app.postMessage(
      JSON.stringify({
        function: 'setScreenOrientation',
        orientation: orientation
      })
    );
  }
}

// 使用传统函数声明代替箭头函数，兼容ES2015
export function qidianLog(clstag: string, ext?: any) {
  try {
    // 使用条件检查代替短路运算符
    if ((window as any).__qd__) {
      (window as any).__qd__.click({
        cls: clstag,
        v: ext || null
      });
    }
  } catch (e) {
    console.log(e);
  }
}

// 使用传统函数声明代替箭头函数，兼容ES2015
export function qidianImpLog(clstag: string, ext?: any) {
  try {
    // 使用条件检查代替短路运算符
    if ((window as any).__qd__) {
      (window as any).__qd__.imp({
        cls: clstag,
        v: ext || null
      });
    }
  } catch (e) {
    console.log(e);
  }
}

/**
 * 统一 SGM 埋点，内部使用 try/catch 防止影响业务逻辑
 */
export function sgmLog(type: string, code: string, msg?: string) {
  try {
    (window as any).__sgm__ &&
      (window as any).__sgm__.custom({ type, code, msg });
  } catch (e) {}
}

// 使用传统函数声明代替箭头函数，兼容ES2015
export function safeWaybillData(waybill: string | undefined) {
  // 使用条件检查代替可选链操作符
  if (!waybill) {
    return '';
  }
  return waybill.replace(/(\d{3})\d{4}/, '$1****');
}

export function openWebPage(url: string) {
  if (window && window.app && window.app.postMessage) {
    window.app.postMessage(
      JSON.stringify({
        function: 'startWebPage',
        url: url
      })
    );
  }
}

export function closeWebPage() {
  localStorage.removeItem('workbench_active_tab');
  if (window && window.app && window.app.postMessage) {
    window.app.postMessage(
      JSON.stringify({
        function: 'exit'
      })
    );
  }
}

/**
 * @description: 防抖函数
 * @param {Function} func 要执行的函数
 * @param {number} wait 等待时间
 * @param {boolean} immediate 是否立即执行
 * @return {*}
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number,
  immediate?: boolean
) {
  var timeout: any, result: any;

  var debounced = function (this: ThisParameterType<T>) {
    var context = this;
    // eslint-disable-next-line prefer-rest-params
    var args = arguments;

    if (timeout) clearTimeout(timeout);
    if (immediate) {
      var callNow = !timeout;
      timeout = setTimeout(function () {
        timeout = null;
      }, wait);
      if (callNow)
        result = func.apply(context, Array.prototype.slice.call(args));
    } else {
      timeout = setTimeout(function () {
        func.apply(context, Array.prototype.slice.call(args));
      }, wait);
    }
    return result;
  };

  (debounced as any).cancel = function () {
    clearTimeout(timeout);
    timeout = null;
  };

  return debounced as ((...args: Parameters<T>) => ReturnType<T>) & {
    cancel: () => void;
  };
}

/**
 * @description: 深拷贝
 * @param {*} source 要拷贝的对象
 * @param {WeakMap} hash 哈希表，用于解决循环引用
 * @return {*}
 */
export function cloneDeep<T>(source: T, hash?: WeakMap<object, any>): T {
  if (!hash) {
    hash = new WeakMap();
  }

  if (source === null || typeof source !== 'object') {
    return source;
  }

  if (hash.has(source as object)) {
    return hash.get(source as object);
  }

  var target: any;

  if (source instanceof Date) {
    target = new Date(source.getTime());
  } else if (source instanceof RegExp) {
    target = new RegExp(source.source, source.flags);
  } else if (Array.isArray(source)) {
    target = [];
    hash.set(source, target);
    for (var i = 0; i < source.length; i++) {
      target[i] = cloneDeep(source[i], hash);
    }
  } else if (Object.prototype.toString.call(source) === '[object Object]') {
    target = {};
    hash.set(source as object, target);
    var keys = Object.keys(source);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i] as keyof T;
      target[key] = cloneDeep(source[key], hash);
    }
  } else {
    target = source;
  }

  return target;
}

// Web Audio API 播放音效，避免直接 audio.play() 被浏览器阻止
let audioContext: AudioContext | null = null;
// 支持多语音：按 URL 缓存解码后的 AudioBuffer 与加载中的 Promise
const audioBufferCache: Map<string, AudioBuffer> = new Map();
const audioLoadingPromises: Map<string, Promise<void>> = new Map();

/**
 * 预加载音频，缓存解码后的 AudioBuffer
 * @param src 音频资源地址（可为打包后的 url 或网络地址）
 */
export function preloadAudio(src: string): Promise<void> {
  // 1. 检查入参
  if (!src) return Promise.resolve();

  // 2. 检查是否已存在buffer和promise的缓存，有则直接取出
  if (audioBufferCache.has(src)) return Promise.resolve();
  if (audioLoadingPromises.has(src)) return audioLoadingPromises.get(src)!;

  // 3. 检查是否有audio的播报上下文
  if (!audioContext) {
    audioContext = new (
      window.AudioContext || (window as any).webkitAudioContext
    )();
  }
  if (!audioContext) return Promise.resolve();

  // 4. 开始加载并解析
  const ctx = audioContext;
  const p = fetch(src)
    .then(function (res) {
      return res.arrayBuffer();
    })
    .then(function (arrayBuffer) {
      return ctx
        ? ctx.decodeAudioData(arrayBuffer)
        : Promise.reject('audioContext is null');
    })
    // 5. 缓存解码后的 AudioBuffer
    .then(function (buffer) {
      audioBufferCache.set(src, buffer);
    })
    .catch(function (err) {
      console.error('音频加载失败', err);
    })
    // 6. 清除 promise 的缓存
    .finally(function () {
      audioLoadingPromises.delete(src);
    });

  // 7. 保存 promise 的缓存
  audioLoadingPromises.set(src, p);
  return p;
}

/**
 * 播放音效
 * @param src 可选，音频资源地址；不传则使用默认提示音
 */
export async function playAudio(src?: string) {
  // 1. 处理入参
  const defaultSrc: string = require('@/assets/audio/beep.mp3');
  const url = src || defaultSrc;

  // 2.校验是否有audio的播报上下文
  if (!audioContext) {
    audioContext = new (
      window.AudioContext || (window as any).webkitAudioContext
    )();
  }
  if (!audioContext) return;

  // 3. 若未缓存buffer则预加载
  if (!audioBufferCache.has(url)) {
    await preloadAudio(url);
    if (!audioBufferCache.has(url)) return; // 加载失败
  }

  // 4. 播放音效
  const buffer = audioBufferCache.get(url)!;
  const source = audioContext.createBufferSource();
  source.buffer = buffer;
  source.connect(audioContext.destination);
  source.start(0);
}

/**
 * 检测录音权限是否开启
 * @returns Promise<{hasPermission: boolean, error?: string}>
 */
export function getUserMediaPermission() {
  return new Promise(function (resolve) {
    const nav: any = window.navigator || {};
    const legacyGetUserMedia =
      nav.getUserMedia ||
      nav.webkitGetUserMedia ||
      nav.mozGetUserMedia ||
      nav.msGetUserMedia;

    // 现代浏览器
    if (nav.mediaDevices && nav.mediaDevices.getUserMedia) {
      nav.mediaDevices
        .getUserMedia({ audio: true, video: false })
        .then(function (stream: MediaStream) {
          if (stream && stream.getAudioTracks) {
            stream.getAudioTracks().forEach(function (t) {
              t.stop();
            });
          }
          resolve({ hasPermission: true });
        })
        .catch(function () {
          resolve({ hasPermission: false, error: '获取录音权限失败' });
        });
    }
    // 旧版浏览器
    else if (legacyGetUserMedia) {
      legacyGetUserMedia.call(
        nav,
        { audio: true, video: false },
        function (stream: MediaStream) {
          if (stream && stream.getAudioTracks) {
            stream.getAudioTracks().forEach(function (t) {
              t.stop();
            });
          }
          resolve({ hasPermission: true });
        },
        function () {
          resolve({ hasPermission: false, error: '获取录音权限失败' });
        }
      );
    }
    // 不支持
    else {
      resolve({ hasPermission: false, error: '浏览器不支持录音功能' });
    }
  });
}

/**
 * 通用异步并发锁：为任意（可能返回 Promise 的）函数添加“同一时刻只允许运行一次”的保护。
 * - 首次调用开始执行；期间再次调用会被直接忽略（返回 undefined）。
 * - 函数同步抛错或异步失败都会在结束时释放锁；错误将原样抛出给调用方。
 * - 不修改原函数入参与返回类型。
 */
export function withAsyncLock<T extends (...args: any[]) => any>(
  fn: T,
  options?: {
    onError?: (e: any) => void; // 错误回调（可选）
    swallowError?: boolean; // 是否吞掉错误，默认 true（安全版）
  }
) {
  var inFlight = false;
  var swallow = !options || options.swallowError !== false; // 默认吞错

  return function (this: ThisParameterType<T>) {
    if (inFlight) return undefined as unknown as ReturnType<T>;
    inFlight = true;

    // eslint-disable-next-line prefer-rest-params
    var args = arguments as IArguments;

    function end() {
      inFlight = false;
    }

    function handleError(err: any) {
      try {
        options && options.onError && options.onError(err);
      } catch (_) {
        // 忽略 onError 自身的异常，避免二次报错
      }
      if (swallow) {
        return undefined as unknown as ReturnType<T>;
      } else {
        throw err;
      }
    }

    try {
      // 使用 apply 调用以保持 this 绑定与参数
      var result: any = fn.apply(this, Array.prototype.slice.call(args));
      // 如果是 Promise，则在 settle 后释放锁
      if (result && typeof result.then === 'function') {
        return (result as Promise<any>).then(
          function (val) {
            end();
            return val;
          },
          function (err) {
            end();
            return handleError(err);
          }
        ) as unknown as ReturnType<T>;
      }
      // 非 Promise：同步结束后释放锁
      end();
      return result as ReturnType<T>;
    } catch (e) {
      // 同步异常也要释放锁
      end();
      return handleError(e);
    }
  } as (...args: Parameters<T>) => ReturnType<T>;
}

/**
 * 转化url的http为https
 * @param {*} url
 * @return {*}
 */
export const transferHttpUrlToHttpsUrl = (url: any) => {
  if (typeof url === 'string') {
    return url.replace('http://', 'https://');
  }
  return null;
};

/**
 * 将对象转化为其键值对的健作为key和value的形式
 * 用于生成枚举
 * @template T
 * @param {T} defs
 * @return {*}
 */
export function makeFieldKeyMap<T extends Record<string, any>>(defs: T) {
  const res = {} as { [K in keyof T]: K };
  for (const k in defs) {
    res[k] = k;
  }
  return res;
}

// 合并值的通用方法
// - 当任一值为 string 时：将两者按 options.separator（默认 ","）拼接（若一方为空则返回另一方）
// - 当任一值为 array 时：将两者转为数组后使用 concat 合并
// - 当值为 object 时：使用 Object.assign({}, existing, current) 合并并返回新对象
// - 其他情况：优先返回 current（如果 current 为 null/undefined 则返回 existing）
export type MergeOptions = {
  separator?: string;
};

export function mergeValues(
  existing: any,
  current: any,
  options?: MergeOptions
): any {
  const separator = options?.separator ?? ',';

  const hasExisting = !isAbnormalValue(existing);
  const hasCurrent = !isAbnormalValue(current);

  if (!hasExisting) return current;
  if (!hasCurrent) return existing;

  // 字符串处理（任一为字符串时按字符串处理）
  if (typeof existing === 'string' || typeof current === 'string') {
    const existingStr = hasExisting ? String(existing) : '';
    const currentStr = hasCurrent ? String(current) : '';
    if (!existingStr) return currentStr;
    if (!currentStr) return existingStr;
    return `${existingStr}${separator}${currentStr}`;
  }

  // 数组处理（任一为数组时按数组合并）
  if (Array.isArray(existing) || Array.isArray(current)) {
    const existingArr = Array.isArray(existing) ? existing : [existing];
    const currentArr = Array.isArray(current) ? current : [current];
    return existingArr.concat(currentArr);
  }

  // 对象处理（优先使用 assign 合并）
  if (typeof existing === 'object' || typeof current === 'object') {
    const existingObj =
      typeof existing === 'object' && !Array.isArray(existing) ? existing : {};
    const currentObj =
      typeof current === 'object' && !Array.isArray(current) ? current : {};
    return Object.assign({}, existingObj, currentObj);
  }

  // fallback：优先返回 current
  return current;
}

/**
 * 把「2025-01-01 09:08:09」格式的时间字符串转换为Date对象
 * @param {string} time
 * @return {*}
 */
export const parseLocalDateTime = (time: string) => {
  if (!time) return null;
  const [datePart, timePart] = time.trim().split(' ');
  if (!datePart || !timePart) return null;

  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  const parts = [year, month, day, hour, minute, second];

  if (parts.some((value) => Number.isNaN(value))) {
    return null;
  }

  return new Date(year, month - 1, day, hour, minute, second || 0);
};

/**
 * 将值转化为布尔值
 * 1为true
 * @param {unknown} value
 * @return {*}  {boolean}
 */
export const normalizeBooleanParam = (value: unknown): boolean => {
  const target = Array.isArray(value) ? value[0] : value;
  if (typeof target === 'string') {
    return target === '1' || target.toLowerCase() === 'true';
  }
  return Boolean(target);
};

/**
 * 对毫秒数进行格式化处理，返回分钟和秒数
 * 用于「正计时」场景下的数字展示
 * @export
 * @param {number} elapsedMs
 * @return {*}  {{
 *   minute: number;
 *   second: number;
 *   elapsedMs: number;
 * }}
 */
export function generateMinutesAndSecondsByElapsedMs(elapsedMs: number): {
  minute: number;
  second: number;
  elapsedMs: number;
} {
  const safeElapsed = Number.isFinite(elapsedMs) ? Math.max(0, elapsedMs) : 0;
  const totalSeconds = Math.floor(safeElapsed / 1000);
  const minute = Math.floor(totalSeconds / 60);
  const second = totalSeconds % 60;
  return { minute, second, elapsedMs: safeElapsed };
}

/**
 * 获取最近xx天的日期范围
 * 返回「秒级时间戳」的时间格式
 * @param {number} dateRangeDays
 * @return {*}
 */
export const getRecentDaysRange = (dateRangeDays: number) => {
  const safeRangeDays = Number.isFinite(dateRangeDays)
    ? Math.max(1, Math.floor(dateRangeDays))
    : 1;
  const now = new Date();
  const endDate = new Date(now);
  const startDate = new Date(now);
  startDate.setDate(startDate.getDate() - (safeRangeDays - 1));

  startDate.setHours(0, 0, 0, 0);
  endDate.setHours(23, 59, 59, 999);

  return {
    startCreateTime: Math.floor(startDate.getTime() / 1000),
    endCreateTime: Math.floor(endDate.getTime() / 1000)
  };
};

/**
 * 将时间戳转化为毫秒数
 * 入参的时间戳可为秒级或毫秒级
 * @param {unknown} [timestamp]
 * @return {*}
 */
export const parseTimestampToMs = (timestamp?: unknown): number | null => {
  if (!timestamp) return null;
  const formatTimestamp = Number(timestamp);
  if (!Number.isFinite(formatTimestamp) || Number.isNaN(formatTimestamp))
    return null;
  if (String(Math.trunc(formatTimestamp)).length <= 10) {
    return formatTimestamp * 1000;
  }
  return formatTimestamp;
};

/**
 * 根据页面id和行为id生成埋点id
 *
 * @param {string} pageId
 * @param {string} actionId
 * @return {*}  {string}
 */
export const generateTrackingId = (
  pageId: string,
  actionId: string
): string => {
  if (!pageId || !actionId) return '';
  return `${pageId}|${actionId}`;
};
